import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from "lucide-react";
import { useKioskActivity } from "@/hooks/useKioskActivity";
import { KioskActivityPrompt } from "@/components/KioskActivityPrompt";
import { useLanguage } from "@/contexts/LanguageContext";
import { UI_TRANSLATIONS } from "@/lib/i18n";

// URL de la API (pdf-proxy migrado de Supabase Edge Functions)
const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

interface PdfViewerProps {
  url: string;
  onClose: () => void;
}

// Declaración de tipos para PDF.js
declare global {
  interface Window {
    pdfjsLib: {
      getDocument: (src: { url: string; withCredentials: boolean }) => {
        promise: Promise<PDFDocumentProxy>;
      };
      GlobalWorkerOptions: {
        workerSrc: string;
      };
    };
  }
}

interface PDFDocumentProxy {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PDFPageProxy>;
}

interface PDFPageProxy {
  getViewport: (options: { scale: number }) => { width: number; height: number };
  render: (renderContext: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
  }) => { promise: Promise<void> };
}

export const PdfViewer = ({ url, onClose }: PdfViewerProps) => {
  const { language } = useLanguage();
  const translations = UI_TRANSLATIONS[language];
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfJsLoaded, setPdfJsLoaded] = useState(false);

  // Kiosk activity handling
  const handleNoResponse = useCallback(() => {
    console.log('⏰ Sin respuesta del usuario en PdfViewer - activando salvapantallas');
    onClose();
    window.dispatchEvent(new Event('forceScreensaver'));
  }, [onClose]);

  const { showPrompt, confirmActivity, remainingSeconds } = useKioskActivity({
    promptInterval: 120000,
    responseTimeout: 15000,
    onNoResponse: handleNoResponse,
    enabled: true
  });

  // Cargar PDF.js desde CDN
  useEffect(() => {
    const loadPdfJs = async () => {
      // Si ya está cargado, no hacer nada
      if (window.pdfjsLib) {
        setPdfJsLoaded(true);
        return;
      }

      try {
        // Cargar el script principal de PDF.js
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.async = true;
        
        script.onload = () => {
          // Configurar el worker
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          console.log('✅ PDF.js cargado correctamente');
          setPdfJsLoaded(true);
        };

        script.onerror = () => {
          console.error('❌ Error al cargar PDF.js');
          setError('Error al cargar el visor de PDF');
          setLoading(false);
        };

        document.head.appendChild(script);
      } catch (err) {
        console.error('❌ Error cargando PDF.js:', err);
        setError('Error al cargar el visor de PDF');
        setLoading(false);
      }
    };

    loadPdfJs();
  }, []);

  // Cargar el documento PDF una vez que PDF.js está listo
  useEffect(() => {
    if (!pdfJsLoaded || !window.pdfjsLib) return;

    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);

        // Construir URL del proxy
        const proxyUrl = `${API_URL}/pdf-proxy?url=${encodeURIComponent(url)}`;
        console.log('📄 Cargando PDF via proxy:', proxyUrl);

        const loadingTask = window.pdfjsLib.getDocument({
          url: proxyUrl,
          withCredentials: false
        });

        const pdf = await loadingTask.promise;
        console.log('✅ PDF cargado, páginas:', pdf.numPages);
        
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        setCurrentPage(1);
        setLoading(false);
      } catch (err) {
        console.error('❌ Error cargando PDF:', err);
        setError('Error al cargar el documento PDF');
        setLoading(false);
      }
    };

    loadPdf();
  }, [url, pdfJsLoaded]);

  // Renderizar la página actual
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current || !containerRef.current) return;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(currentPage);
        const canvas = canvasRef.current!;
        const container = containerRef.current!;
        const context = canvas.getContext('2d');

        if (!context) return;

        // Calcular escala para ajustar al contenedor
        const containerWidth = container.clientWidth - 40; // Padding
        const containerHeight = container.clientHeight - 80; // Espacio para controles

        const viewport = page.getViewport({ scale: 1 });
        const scaleX = containerWidth / viewport.width;
        const scaleY = containerHeight / viewport.height;
        const optimalScale = Math.min(scaleX, scaleY) * scale;

        const scaledViewport = page.getViewport({ scale: optimalScale });

        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: scaledViewport
        };

        await page.render(renderContext).promise;
        console.log(`✅ Página ${currentPage} renderizada`);
      } catch (err) {
        console.error('❌ Error renderizando página:', err);
      }
    };

    renderPage();
  }, [pdfDoc, currentPage, scale]);

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 1.25));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 1.0));
  };

  const handleClose = useCallback(() => {
    console.log('🏠 Botón INICIO presionado en PdfViewer');
    onClose();
  }, [onClose]);

  return (
    <>
      <div className="webview-container bg-black">
        {/* Contenedor del PDF */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-hidden flex flex-col items-center p-4"
        >
          {loading && (
            <div className="flex flex-col items-center justify-center h-full text-white gap-4">
              <Loader2 className="w-12 h-12 animate-spin" />
              <p className="text-lg">{translations.loading}</p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-full text-white gap-4">
              <p className="text-lg text-red-400">{error}</p>
              <button
                onClick={handleClose}
                className="px-6 py-3 bg-primary text-white rounded-lg"
              >
                {translations.returnNow}
              </button>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Controles superiores */}
              <div className="flex items-center justify-center gap-4 mb-4 bg-black/50 rounded-lg p-2">
                <button
                  onClick={goToPrevPage}
                  disabled={currentPage <= 1}
                  className="p-2 bg-white/20 rounded-lg disabled:opacity-30 text-white"
                  aria-label="Página anterior"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                <span className="text-white text-lg min-w-[100px] text-center">
                  {currentPage} / {totalPages}
                </span>

                <button
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                  className="p-2 bg-white/20 rounded-lg disabled:opacity-30 text-white"
                  aria-label="Página siguiente"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                <div className="w-px h-6 bg-white/30 mx-2" />

                <button
                  onClick={zoomOut}
                  disabled={scale <= 1.0}
                  className="p-2 bg-white/20 rounded-lg disabled:opacity-30 text-white"
                  aria-label="Reducir zoom"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>

                <span className="text-white text-sm min-w-[50px] text-center">
                  {Math.round(scale * 100)}%
                </span>

                <button
                  onClick={zoomIn}
                  disabled={scale >= 1.25}
                  className="p-2 bg-white/20 rounded-lg disabled:opacity-30 text-white"
                  aria-label="Aumentar zoom"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
              </div>

              {/* Canvas del PDF */}
              <div className="flex-1 overflow-auto w-full flex items-center justify-start">
                <canvas 
                  ref={canvasRef} 
                  className="shadow-2xl rounded-lg"
                />
              </div>
            </>
          )}
        </div>

        {/* Área del botón INICIO */}
        <div className="webview-button-area">
          <button
            className="webview-home-button-inline"
            onClick={handleClose}
            onTouchEnd={(e) => {
              e.preventDefault();
              console.log('👆 TouchEnd en botón INICIO (PdfViewer)');
              handleClose();
            }}
            type="button"
          >
            {translations.home}
          </button>
        </div>
      </div>

      {showPrompt && (
        <KioskActivityPrompt
          isVisible={showPrompt}
          onConfirm={confirmActivity}
          remainingSeconds={remainingSeconds}
        />
      )}
    </>
  );
};
